import * as React from "react";
import { Modal, Button, Form, Grid } from "semantic-ui-react";
import moment from "moment";

import { Input } from "@neworbit/simpleui-input";

import { IsLoadingButton } from "@teamprevent/common/IsLoadingButton";
import { GENDER_OPTIONS } from "@teamprevent/common/utils";

import { validators } from "not-valid";

import { dateOfBirthInRange } from "@teamprevent/common/validators/dateOfBirthInRange";

import { AddUnknownEmployeeModel, emptyAddUnknownEmployeeModel, MatchingEmployeeModel } from "../models";
import { WorkGroup } from "../../../crud/workGroup";
import { caseTypeOptions } from "../../../healthSurveillance";

import { OhCaseInformationModal } from "./OhCaseInformationModal";

import "../styles/add-unknown-employee-modal.scss";

interface Props {
    searchForEmployee: (employeeName: string) => Promise<MatchingEmployeeModel[]>;
    workGroups: WorkGroup[];
    saveRequestNames: string[];
    onSubmit: (model: AddUnknownEmployeeModel) => Promise<void>;
    customerId: string;
    clinicId: string;
}

export const AddUnknownEmployeeModal: React.FunctionComponent<Props> = (props: Props) => {

    const [isOpen, setIsOpen] = React.useState<boolean>(false);
    const [addingNew, setAddingNew] = React.useState<boolean>(false);
    const [matchedEmployees, setMatchedEmployees] = React.useState<MatchingEmployeeModel[]>([]);
    const [employeeName, setEmployeeName] = React.useState<string>("");
    const [caseTypes, setCaseTypes] = React.useState<string>("Both");
    const [workGroups, setWorkGroups] = React.useState<string[]>([]);
    const [showErrors, setShowErrors] = React.useState<boolean>(false);
    const [unknownEmployeeModel, setUnknownEmployeeModel] = React.useState<AddUnknownEmployeeModel>(emptyAddUnknownEmployeeModel());

    const resetState = React.useCallback(() => {
        setIsOpen(false);
        setAddingNew(false);
        setMatchedEmployees([]);
        setEmployeeName("");
        setCaseTypes("Both");
        setWorkGroups([]);
        setShowErrors(false);
        setUnknownEmployeeModel(emptyAddUnknownEmployeeModel());
    }, []);

    const isDobValid = (dob: moment.Moment | null | undefined): boolean => {
        if (!dob) {
            return false;
        }

        return dateOfBirthInRange(dob).type === "pass";
    };

    const isNewEmployeeModelInvalid = (): boolean => {
        return !isDobValid(unknownEmployeeModel.dateOfBirth)
            || !unknownEmployeeModel.title
            || !unknownEmployeeModel.firstName
            || !unknownEmployeeModel.lastName
            || !unknownEmployeeModel.gender
            || !caseTypes
            || !(workGroups?.length > 0);
    };

    const onSubmit = () => {
        setShowErrors(true);

        if (isNewEmployeeModelInvalid()) {
            return;
        }

        props.onSubmit(unknownEmployeeModel).then(() => resetState());
    };

    const open = React.useCallback(() => setIsOpen(true), []);

    const searchForEmployee = () => {
        props.searchForEmployee(employeeName).then(employees => setMatchedEmployees(employees));
    };

    const addingNewEmployee = React.useCallback(() => {
        setAddingNew(true);
        setWorkGroups([]);
        setUnknownEmployeeModel({ ...unknownEmployeeModel, matchingEmployee: undefined, workGroups: [] });
    }, [unknownEmployeeModel]);

    const onChangeEmployeeName = (newEmployeeName: string) => {
        setUnknownEmployeeModel({ ...unknownEmployeeModel, employeeName: newEmployeeName });
        setEmployeeName(newEmployeeName);
    };

    const onChangeTitle = (title: string) => setUnknownEmployeeModel({ ...unknownEmployeeModel, title });

    const onChangeFirstName = (firstName: string) => setUnknownEmployeeModel({ ...unknownEmployeeModel, firstName });

    const onChangeLastName = (lastName: string) => setUnknownEmployeeModel({ ...unknownEmployeeModel, lastName });

    const onChangeSecondaryReference = (secondaryReference: string) => setUnknownEmployeeModel({ ...unknownEmployeeModel, secondaryReference });

    const onChangeGender = (gender: string) => setUnknownEmployeeModel({ ...unknownEmployeeModel, gender });

    const onChangeEmailAddress = (emailAddress: string) => setUnknownEmployeeModel({ ...unknownEmployeeModel, emailAddress });

    const onChangeJobTitle = (jobTitle: string) => setUnknownEmployeeModel({ ...unknownEmployeeModel, jobTitle });

    const onChangeDateOfBirth = (dateOfBirth: moment.Moment) => setUnknownEmployeeModel({ ...unknownEmployeeModel, dateOfBirth });

    const onChangeAddressLine1 = (addressLine1: string) => setUnknownEmployeeModel({ ...unknownEmployeeModel, addressLine1 });

    const onChangePostcode = (postcode: string) => setUnknownEmployeeModel({ ...unknownEmployeeModel, postcode });

    const onChangeCaseTypes = (newCaseTypes: string) => {
        setUnknownEmployeeModel({ ...unknownEmployeeModel, caseTypes: newCaseTypes });
        setCaseTypes(newCaseTypes);
    };

    const onChangeWorkGroups = (newWorkGroups: string[]) => {
        setUnknownEmployeeModel({ ...unknownEmployeeModel, workGroups: newWorkGroups });
        setWorkGroups(newWorkGroups);
    };

    const onChangeMatchingEmployee = (employeeId: any) => {
        if (employeeId) {
            const matchedEmployee = matchedEmployees.find(e => e.id === employeeId);
            const matchedEmployeeWorkGroups = matchedEmployee?.workGroups || [];
            setWorkGroups(matchedEmployeeWorkGroups);
            setUnknownEmployeeModel({
                ...unknownEmployeeModel,
                ...matchedEmployee,
                matchingEmployee: employeeId,
                workGroups: matchedEmployeeWorkGroups
            });
        } else {
            setUnknownEmployeeModel({ ...unknownEmployeeModel, matchingEmployee: employeeId });
        }
    };

    const matchingEmployees = matchedEmployees.map(e => (
        {
            text: `${e.firstName} ${e.lastName} - ${e.dateOfBirth ? e.dateOfBirth.format("DD/MM/YYYY") : "No DOB"}`,
            value: e.id,
            workGroups: e.workGroups
        }
    ));

    return (
        <Modal
            open={isOpen}
            trigger={<Button content="Add unknown Employee" onClick={open} />}
        >
            <Modal.Header content="Add unknown Employee" />
            <Modal.Content>
                <Form>
                    {!addingNew ? (
                        <>
                            <Grid>
                                <Grid.Row>
                                    <Grid.Column width="12">
                                        <Input.Text
                                            key={"employeeName"}
                                            label={"Search Employee Name"}
                                            value={employeeName}
                                            showErrors={showErrors}
                                            // eslint-disable-next-line react/jsx-no-bind
                                            onChange={onChangeEmployeeName}
                                        />
                                    </Grid.Column>
                                    <Grid.Column width="4">
                                        <Button content="Search"
                                            // eslint-disable-next-line react/jsx-no-bind
                                            onClick={searchForEmployee}
                                            className="centered-button"
                                        />
                                    </Grid.Column>
                                </Grid.Row>
                            </Grid>
                            {matchedEmployees?.length > 0 ? (
                                <Grid>
                                    <Grid.Row>
                                        <Grid.Column>
                                            <Input.Dropdown
                                                key={"matchingEmployee"}
                                                required
                                                value={unknownEmployeeModel.matchingEmployee}
                                                showErrors={showErrors}
                                                label="Matching employee"
                                                options={matchingEmployees}
                                                dynamicOptions
                                                // eslint-disable-next-line react/jsx-no-bind
                                                onChange={onChangeMatchingEmployee}
                                            />
                                        </Grid.Column>
                                    </Grid.Row>
                                    <Grid.Row>
                                        <Grid.Column width={unknownEmployeeModel.matchingEmployee ? 8 : 12}>
                                            <p>If there isn't matching employee, search again, or create new employee</p>
                                        </Grid.Column>
                                        {unknownEmployeeModel.matchingEmployee &&
                                                <Grid.Column width="4">
                                                    <OhCaseInformationModal
                                                        clinicId={props.clinicId}
                                                        customerId={props.customerId}
                                                        employeeRecordId={unknownEmployeeModel.matchingEmployee}
                                                        continueWithAction={resetState}
                                                    />
                                                </Grid.Column>
                                        }
                                        <Grid.Column width="4">
                                            <Button content="Create new Employee" onClick={addingNewEmployee} className="centered-button" />
                                        </Grid.Column>
                                    </Grid.Row>
                                </Grid>
                            ) : (
                                <Grid>
                                    <Grid.Row>
                                        <Grid.Column width="12">
                                            <p>No employees with provided employee name found. Search again, or create new employee</p>
                                        </Grid.Column>
                                        <Grid.Column width="4">
                                            <Button content="Create new Employee" onClick={addingNewEmployee} className="centered-button" />
                                        </Grid.Column>
                                    </Grid.Row>
                                </Grid>
                            )}
                        </>
                    ) : (
                        <>
                            <Input.Text
                                key={"secondaryReference"}
                                value={unknownEmployeeModel.secondaryReference}
                                showErrors={showErrors}
                                label="Employee Unique ID"
                                // eslint-disable-next-line react/jsx-no-bind
                                onChange={onChangeSecondaryReference}
                                validation={[ validators.validLength({ max: 20 }) ]}
                            />
                            <Input.Text
                                key={"title"}
                                required
                                value={unknownEmployeeModel.title}
                                showErrors={showErrors}
                                label="Title"
                                // eslint-disable-next-line react/jsx-no-bind
                                onChange={onChangeTitle}
                            />
                            <Input.Text
                                key={"firstName"}
                                required
                                value={unknownEmployeeModel.firstName}
                                showErrors={showErrors}
                                label="First Name"
                                // eslint-disable-next-line react/jsx-no-bind
                                onChange={onChangeFirstName}
                            />
                            <Input.Text
                                key={"lastName"}
                                required
                                value={unknownEmployeeModel.lastName}
                                showErrors={showErrors}
                                label="Last Name"
                                // eslint-disable-next-line react/jsx-no-bind
                                onChange={onChangeLastName}
                            />
                            <Input.Dropdown
                                key={"gender"}
                                required
                                placeholder="Please select"
                                value={unknownEmployeeModel.gender}
                                showErrors={showErrors}
                                options={GENDER_OPTIONS}
                                label="Gender"
                                // eslint-disable-next-line react/jsx-no-bind
                                onChange={onChangeGender}
                            />
                            <Input.Date
                                key={"dateOfBirth"}
                                required
                                label={"Date Of Birth"}
                                value={unknownEmployeeModel.dateOfBirth}
                                showErrors={showErrors}
                                // eslint-disable-next-line react/jsx-no-bind
                                onChange={onChangeDateOfBirth}
                                validation={[ dateOfBirthInRange ]}
                            />
                            <Input.Text
                                key={"emailAddress"}
                                value={unknownEmployeeModel.emailAddress}
                                showErrors={showErrors}
                                label="Email Address"
                                // eslint-disable-next-line react/jsx-no-bind
                                onChange={onChangeEmailAddress}
                            />
                            <Input.Text
                                key={"addressLine1"}
                                value={unknownEmployeeModel.addressLine1}
                                showErrors={showErrors}
                                label="Address Line 1"
                                // eslint-disable-next-line react/jsx-no-bind
                                onChange={onChangeAddressLine1}
                            />
                            <Input.Text
                                key={"postcode"}
                                required
                                value={unknownEmployeeModel.postcode}
                                showErrors={showErrors}
                                label="Postcode"
                                // eslint-disable-next-line react/jsx-no-bind
                                onChange={onChangePostcode}
                            />
                            <Input.Text
                                key={"jobTitle"}
                                required
                                value={unknownEmployeeModel.jobTitle}
                                showErrors={showErrors}
                                label="Job Title"
                                // eslint-disable-next-line react/jsx-no-bind
                                onChange={onChangeJobTitle}
                            />
                        </>
                    )}
                    <Input.Dropdown
                        key={"caseTypes"}
                        required
                        value={caseTypes}
                        showErrors={showErrors}
                        label="Case type"
                        options={caseTypeOptions}
                        // eslint-disable-next-line react/jsx-no-bind
                        onChange={onChangeCaseTypes}
                    />
                    <Input.DropdownMulti
                        key={"workGroups"}
                        required
                        value={workGroups}
                        showErrors={showErrors}
                        label="Work Groups"
                        options={props.workGroups && props.workGroups.map(g => ({ text: g.name, value: g.id }))}
                        // eslint-disable-next-line react/jsx-no-bind
                        onChange={onChangeWorkGroups}
                        search
                    />
                </Form>
            </Modal.Content>
            <Modal.Actions>
                <Button onClick={resetState} content="Cancel" />
                <IsLoadingButton
                    requestNames={props.saveRequestNames}
                    // eslint-disable-next-line react/jsx-no-bind
                    onClick={onSubmit}
                    content="Save"
                />
            </Modal.Actions>
        </Modal>
    );
};
